/**
 * Free Gift offer — product-triggered gift, parallel to gift-with-purchase.js.
 *
 * When the cart contains one or more "participating" products the customer can
 * pick ONE gift product (a $0 duplicate product) from a configurable list.
 * Line-item property used: _free_gift = 'true'.
 *
 * - The gift line is not removable from the cart UI (the cart sections hide the
 *   remove button + quantity input for it). Removal happens only here, automatically,
 *   when no participating product remains or when the configured gift list no longer
 *   includes the current gift ("stale"/orphan).
 * - When the gift product has more than one option, the customer picks one. When the
 *   configuration is a single product with a single available variant, the gift is
 *   auto-added on init (no picker shown).
 * - When the promotion is switched off (window.themeGiftConfig.freeGiftEnabled === false)
 *   any leftover gift line is swept from the cart: the cart UI hides its remove button, so
 *   without this the customer is left with an item nobody can take out.
 * - Caps the cart at ONE gift line in total, quantity 1 (covers a customer who finds
 *   the $0 product's PDP and adds several, and the case of two different gift products
 *   ending up in the same cart), preferring the line that carries the _free_gift property.
 * - Once a gift is in the cart the bar hides itself: the "change gift" link lives on the
 *   gift's cart line, in the slot where every other line keeps its quantity and remove
 *   controls. The link brings the bar back with the picker open and the current choice
 *   marked; picking another one swaps it and the bar tucks away again. When there is
 *   nothing to pick (auto-add, or every gift out of stock) the bar stays, showing the
 *   confirmation, because there is no change to offer.
 */
(function () {
  'use strict';

  var isProcessing = false;
  var pendingRemovalKey = null;
  var isEnforcingQuantity = false;

  // --- Helpers ---------------------------------------------------------------

  function getGiftProductIds() {
    // window.themeGiftConfig is rendered globally (in theme-globals.liquid) so the
    // quantity cap works on /cart and even when no participating product is present.
    if (window.themeGiftConfig && Array.isArray(window.themeGiftConfig.freeGiftProductIds)) {
      return window.themeGiftConfig.freeGiftProductIds.map(String);
    }
    var source = document.querySelector('[data-free-gift]');
    if (!source) return [];
    var raw = source.dataset.fgGiftProductIds || '';
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function isFreeGiftLine(item) {
    return !!(item && item.properties && item.properties._free_gift === 'true');
  }

  function fetchCart() {
    return fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Failed to fetch cart'); return r.json(); });
  }

  function findFreeGiftItem(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isFreeGiftLine(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  // La pagina /cart no escucha los eventos del DOM que emitimos: se refresca sola por el
  // bus interno del tema, al que no tenemos acceso. Cuando quitamos una linea por nuestra
  // cuenta hay que repintarla, o se queda mostrando un regalo que ya no esta en el carrito.
  function refreshCartPage() {
    var container = document.querySelector('[data-section-type="cart"]');
    if (!container) return; // no estamos en /cart
    var sectionId = container.dataset.sectionId;
    if (!sectionId) { window.location.reload(); return; }

    var base = (window.theme && window.theme.routes && window.theme.routes.cart && window.theme.routes.cart.base) || '/cart';

    fetch(base + '?section_id=' + sectionId)
      .then(function (r) { if (!r.ok) throw new Error('Failed to fetch cart section'); return r.text(); })
      .then(function (text) {
        var source = new DOMParser().parseFromString(text, 'text/html');
        var sourceItems = source.querySelector('[js-cart-items]');
        var targetItems = container.querySelector('[js-cart-items]');
        var sourceSubtotal = source.querySelector('[js-cart-footer-subtotal-wrapper]');
        var targetSubtotal = container.querySelector('[js-cart-footer-subtotal-wrapper]');

        if (sourceItems && targetItems && sourceSubtotal && targetSubtotal) {
          targetItems.innerHTML = sourceItems.innerHTML;
          targetSubtotal.outerHTML = sourceSubtotal.outerHTML;
          return;
        }

        // No tocamos [data-free-shipping-bar]: su mensaje lo pinta el JS del tema a partir
        // de data-cart-total, y como los regalos son de $0, quitarlos no mueve el total.
        // El carrito quedo vacio: el tema cambia toda la seccion por el estado "sin articulos".
        var sourceContainer = source.querySelector('[data-section-type="cart"]');
        if (sourceContainer) container.innerHTML = sourceContainer.innerHTML;
        else window.location.reload();
      })
      .catch(function (err) {
        console.error('FreeGift: cart page refresh error', err);
        window.location.reload();
      });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { if (!r.ok) throw new Error('Failed to change cart line'); return r.json(); });
  }

  // --- Promocion apagada: retirar los regalos huerfanos -----------------------

  // El carrito esconde el boton de eliminar y el control de cantidad de las lineas de
  // regalo porque este script se encarga de retirarlas. Cuando la promocion se apaga, el
  // snippet deja de pintar la barra y ese encargado desaparece: sin esto, la clienta se
  // queda con un articulo que no puede quitar por ningun medio. Se apoya en la propiedad
  // de linea, no en la lista de productos, para que siga funcionando aunque la lista se
  // haya vaciado en el admin.
  function sweepGiftsWhenDisabled() {
    var cfg = window.themeGiftConfig;
    // Solo actuamos ante un `false` explicito. Si la bandera no viene (una plantilla vieja
    // en cache, u otro contexto), no tocamos nada: borrar por omision es peor que no borrar.
    if (!cfg || cfg.freeGiftEnabled !== false) return;
    if (isProcessing || isEnforcingQuantity) return;

    isEnforcingQuantity = true;
    fetchCart()
      .then(function (cart) {
        var huerfanas = cart.items.filter(isFreeGiftLine);
        if (!huerfanas.length) return null;
        return huerfanas.reduce(function (p, it) {
          return p.then(function () { return changeLine(it.key, 0); });
        }, Promise.resolve()).then(function () {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          refreshCartPage();
        });
      })
      .catch(function (err) { console.error('FreeGift: sweep error', err); })
      .finally(function () { isEnforcingQuantity = false; });
  }

  // --- Quantity cap (one gift line in the cart, quantity 1) ------------------

  function enforceGiftQuantities() {
    if (isEnforcingQuantity || isProcessing) return;
    var ids = getGiftProductIds();
    if (!ids.length) return;

    isEnforcingQuantity = true;
    fetchCart()
      .then(function (cart) {
        // Todas las lineas de cualquiera de los productos de regalo, en el orden del carrito.
        var giftLines = cart.items.filter(function (it) {
          return ids.indexOf(String(it.product_id)) !== -1;
        });
        if (!giftLines.length) return null;

        // El cliente elige UNO: solo puede quedar una linea de regalo en total, con cantidad 1.
        // Se prefiere la que lleva la propiedad _free_gift (la que puso el selector).
        var keeper = null;
        for (var i = 0; i < giftLines.length; i++) {
          if (isFreeGiftLine(giftLines[i])) { keeper = giftLines[i]; break; }
        }
        if (!keeper) keeper = giftLines[0];

        var changes = [];
        giftLines.forEach(function (it) {
          if (it.key === keeper.key) {
            if (it.quantity !== 1) changes.push({ key: it.key, quantity: 1 });
          } else {
            changes.push({ key: it.key, quantity: 0 });
          }
        });

        if (!changes.length) return null;
        return changes.reduce(function (p, ch) {
          return p.then(function () { return changeLine(ch.key, ch.quantity); });
        }, Promise.resolve()).then(function () {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          refreshCartPage();
        });
      })
      .catch(function (err) { console.error('FreeGift: enforce error', err); })
      .finally(function () { isEnforcingQuantity = false; });
  }

  function onCartChanged() {
    sweepGiftsWhenDisabled();
    enforceGiftQuantities();
  }

  document.addEventListener('cart:updated', onCartChanged);
  document.addEventListener('quick-cart:updated', onCartChanged);

  // --- Init / state machine --------------------------------------------------

  function initFreeGift(container) {
    if (!container) return;

    var hasParticipating = container.dataset.fgHasParticipating === 'true';
    var giftInCart = container.dataset.fgGiftInCart === 'true';
    var staleGiftInCart = container.dataset.fgStaleGiftInCart === 'true';
    var staleGiftLineItemKey = container.dataset.fgStaleGiftLineItemKey;
    var giftProductTitle = container.dataset.fgGiftProductTitle || '';
    var eligibleMessage = (container.dataset.fgEligibleMessage || '').replace('{{ product_name }}', giftProductTitle);
    var addedMessage = (container.dataset.fgAddedMessage || '').replace('{{ product_name }}', giftProductTitle);

    var messageEl = container.querySelector('.free-gift-bar__message');
    var picker = container.querySelector('[data-fg-picker]');
    if (!messageEl) return;

    // Orphan gift (gift list changed, or no participating product remains) → remove it.
    // Hide the bar visually while the removal is in flight so the customer never sees a
    // blank strip if the round-trip stalls.
    if (staleGiftInCart && staleGiftLineItemKey) {
      container.style.display = 'none';
      removeGiftFromCart(staleGiftLineItemKey);
      return;
    }

    // Defensive: the snippet should only render the bar when there is something to show,
    // but if it slips through (e.g. mid-update DOM), don't leave a blank strip.
    if (!hasParticipating) {
      container.style.display = 'none';
      return;
    }

    // Reaching this point means we WILL paint content — make sure the bar is visible
    // (it could have been hidden by a previous stale-path init on the same container).
    container.style.display = '';

    if (!giftInCart) {
      // Auto-add path: when there is only one gift product with a single available
      // variant, skip the picker entirely and add it on the customer's behalf.
      var autoAdd = container.dataset.fgAutoAdd === 'true';
      var autoAddVariantId = container.dataset.fgAutoAddVariantId;
      if (autoAdd && autoAddVariantId && !isProcessing) {
        setMessage(messageEl, addedMessage);
        if (picker) picker.hidden = true;
        container.classList.remove('free-gift-bar--eligible');
        container.classList.add('free-gift-bar--added');
        addGiftToCart(autoAddVariantId, container);
        return;
      }

      // Eligible, no gift yet — show the picker (also covers "customer removed the gift").
      setMessage(messageEl, eligibleMessage);
      if (picker) picker.hidden = false;
      container.classList.remove('free-gift-bar--added');
      container.classList.add('free-gift-bar--eligible');
      bindPicker(container);
    } else {
      // El regalo ya esta en el carrito: la barra arranca colapsada. Se muestra el aviso
      // de confirmacion y un enlace para cambiarlo, en vez del selector entero, que se
      // comia unos 350px del carrito y empujaba fuera de vista lo que la clienta compra.
      //
      // El mensaje sigue al modo, y esa es la regla que evita la contradiccion original:
      // colapsado es un estado cerrado y el aviso en pasado encaja; desplegado es un
      // estado de eleccion y ahi manda la instruccion. Nunca se ve un "ya esta hecho"
      // encima de un selector que invita a seguir eligiendo.
      container.classList.remove('free-gift-bar--eligible');
      container.classList.add('free-gift-bar--added');
      renderGiftInCart(container, messageEl, addedMessage, eligibleMessage);
      bindPicker(container);
    }
  }

  // Con el regalo puesto la barra tiene dos modos.
  //
  // Recogida: la barra desaparece entera. Su unico cometido tras elegir era hospedar el
  // enlace de cambiar, y ese enlace vive ahora en la linea del regalo, en el hueco donde
  // las demas lineas tienen su cantidad y su "Eliminar". El bloque promocional existe
  // mientras hay una oferta que atender; cumplida, se convierte en una linea del carrito.
  // La confirmacion la dan el GRATIS, el "Regalo con tu compra" y el contador del header.
  //
  // Desplegada: reaparece con la instruccion y el selector, con la elegida marcada.
  //
  // Sin opciones que ofrecer -auto-agregado, o todos los regalos agotados- no hay nada que
  // cambiar: ahi la barra se queda con el aviso, que es el uso que le da sentido.
  //
  // El modo se guarda en el contenedor a proposito. Desplegar cambia el texto del mensaje,
  // y eso es una mutacion dentro del carrito que despierta al MutationObserver y relanza
  // initFreeGift; sin recordar el modo, la barra se volvia a recoger sola en cuanto la
  // clienta la abria. Como el drawer entrega un contenedor nuevo en cada repintado real,
  // la marca no sobrevive a un cambio de regalo: ahi vuelve a recogerse, que es lo que se
  // busca, porque el regalo nuevo ya se ve en el carrito.
  function renderGiftInCart(container, messageEl, addedMessage, eligibleMessage) {
    var picker = container.querySelector('[data-fg-picker]');
    var hasOptions = !!picker && pickerButtons(container).length > 0;
    var expanded = hasOptions && container.dataset.fgExpanded === 'true';

    // El enlace del item solo tiene sentido si hay algo a lo que cambiar.
    document.querySelectorAll('[data-fg-change]').forEach(function (b) {
      b.hidden = !hasOptions;
    });

    // Cerrar solo se ofrece con el selector abierto: es el unico estado reversible.
    var close = container.querySelector('[data-fg-close]');
    if (close) close.hidden = !expanded;

    if (hasOptions && !expanded) {
      container.style.display = 'none';
      if (picker) picker.hidden = true;
      return;
    }

    container.style.display = '';
    setMessage(messageEl, expanded ? eligibleMessage : addedMessage);
    if (picker) picker.hidden = !expanded;
    if (expanded) markSelectedGift(container);
  }

  // Cerrar sin cambiar: se vuelve al estado recogido. Salir del selector no deberia
  // costar mas que entrar, y hasta ahora la unica salida era volver a elegir el regalo
  // que ya se tenia: funcionaba, pero habia que adivinarlo.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fg-close]');
    if (!btn) return;
    e.preventDefault();

    var container = btn.closest('[data-free-gift]');
    if (!container) return;
    delete container.dataset.fgExpanded;
    initFreeGift(container);
  });

  // El enlace vive en la linea del carrito, que se repinta constantemente, asi que se
  // escucha por delegacion en el documento en vez de enlazarlo a cada boton.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fg-change]');
    if (!btn) return;
    e.preventDefault();

    var container = document.querySelector('[data-free-gift]');
    if (!container) return;
    container.dataset.fgExpanded = 'true';

    // En /cart el selector no esta en la pagina: vive en el drawer, que el layout monta
    // en todas partes pero oculto. Abrirlo es la forma de llegar a el, y lo hacemos por
    // el icono del carrito para reutilizar el manejador del propio tema.
    var enPaginaCarrito = !!document.querySelector('[data-section-type="cart"]');
    if (enPaginaCarrito) {
      var icono = document.querySelector('[data-js-cart-icon]');
      if (icono) icono.click();
    }

    initFreeGift(container);
    // Al reaparecer, la barra empuja el contenido: hay que llevar la vista al selector.
    var picker = container.querySelector('[data-fg-picker]');
    if (picker) {
      setTimeout(function () {
        picker.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, enPaginaCarrito ? 400 : 0);
    }
  });

  // Escribir innerHTML reemplaza los nodos hijos aunque el texto sea identico, y cada
  // reemplazo despierta al observador del carrito. Escribimos solo cuando cambia.
  function setMessage(messageEl, html) {
    if (messageEl.innerHTML !== html) messageEl.innerHTML = html;
  }

  // Marca visualmente cual es el regalo que esta en el carrito.
  function markSelectedGift(container) {
    var currentVariantId = container.dataset.fgGiftVariantId || '';
    pickerButtons(container).forEach(function (b) {
      var isCurrent = currentVariantId !== '' && b.dataset.fgVariantId === currentVariantId;
      if (b.classList.contains('free-gift-bar__swatch')) {
        b.classList.toggle('free-gift-bar__swatch--selected', isCurrent);
      } else {
        b.classList.toggle('free-gift-bar__card--selected', isCurrent);
      }
      b.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
  }

  function pickerButtons(container) {
    return container.querySelectorAll('.free-gift-bar__card[data-fg-variant-id], .free-gift-bar__swatch[data-fg-variant-id]');
  }

  function bindPicker(container) {
    // initFreeGift corre en cada repintado del drawer; sobre un contenedor que no se
    // reemplazo, volver a enlazar duplicaria los listeners.
    if (container.dataset.fgPickerBound === 'true') return;
    container.dataset.fgPickerBound = 'true';

    pickerButtons(container).forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isProcessing) return;
        var variantId = btn.dataset.fgVariantId;
        if (!variantId) return;
        addGiftToCart(variantId, container);
      });
    });
  }

  function addGiftToCart(variantId, container) {
    if (isProcessing) return;
    isProcessing = true;
    pendingRemovalKey = null;

    var buttons = pickerButtons(container);
    buttons.forEach(function (b) { b.disabled = true; });

    // La marca la pone markSelectedGift leyendo este dato, no el clic directamente, y ese
    // orden importa. El dato viene del servidor con la variante anterior, y mientras el
    // intercambio viaja el carrito muta y se relanza initFreeGift: al releerlo, la marca
    // saltaba de vuelta a la opcion vieja durante un instante. Adelantar aqui el dato deja
    // marca y origen de verdad en el mismo sitio, asi que los init intermedios confirman
    // la eleccion nueva en vez de deshacerla. Si el intercambio falla, se restaura.
    var variantIdPrevio = container.dataset.fgGiftVariantId || '';
    container.dataset.fgGiftVariantId = variantId;
    markSelectedGift(container);

    var variantIdInt = parseInt(variantId, 10);

    function addNew() {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantIdInt, quantity: 1, properties: { _free_gift: 'true' } }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('Failed to add gift'); return r.json(); })
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          if (data.items && data.items.length && data.items[0].quantity > 1) {
            return changeLine(data.items[0].key, 1);
          }
        });
    }

    fetchCart()
      .then(function (cart) {
        var existing = findFreeGiftItem(cart);
        if (existing) {
          if (existing.variant_id === variantIdInt) {
            if (existing.quantity > 1) {
              return changeLine(existing.key, 1).then(function () {
                document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
              });
            }
            // Already in cart with the right variant — just refresh the drawer.
            document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
            return;
          }
          // Different variant/product → swap: remove the old gift line, then add the new one.
          return changeLine(existing.key, 0).then(addNew);
        }
        return addNew();
      })
      .catch(function (err) {
        console.error('FreeGift: add error', err);
        buttons.forEach(function (b) { b.disabled = false; });
        container.dataset.fgGiftVariantId = variantIdPrevio;
        markSelectedGift(container);
      })
      .finally(function () {
        isProcessing = false;
        // Si el drawer no se repinta, los botones quedarian inutilizables para siempre.
        buttons.forEach(function (b) { b.disabled = false; });
        refreshCartPage();
        processPendingRemoval();
        // Safety: a normal line of the gift product may already exist — cap the total at 1.
        setTimeout(enforceGiftQuantities, 0);
      });
  }

  function processPendingRemoval() {
    if (pendingRemovalKey) {
      var key = pendingRemovalKey;
      pendingRemovalKey = null;
      removeGiftFromCart(key);
    }
  }

  function removeGiftFromCart(lineItemKey) {
    if (!lineItemKey) return;
    if (isProcessing) { pendingRemovalKey = lineItemKey; return; }
    isProcessing = true;
    pendingRemovalKey = null;

    changeLine(lineItemKey, 0)
      .then(function () {
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
        refreshCartPage();
      })
      .catch(function (err) { console.error('FreeGift: remove error', err); })
      .finally(function () {
        isProcessing = false;
        processPendingRemoval();
      });
  }

  // --- Bootstrap + observers -------------------------------------------------

  function initAll() {
    // Con la promocion apagada no hay barra que inicializar, pero si puede haber regalos
    // huerfanos en un carrito guardado de cuando estaba encendida.
    sweepGiftsWhenDisabled();
    document.querySelectorAll('[data-free-gift]').forEach(initFreeGift);
  }

  var initScheduled = false;
  function scheduleInit() {
    if (initScheduled) return;
    initScheduled = true;
    requestAnimationFrame(function () { initScheduled = false; initAll(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  var observeTarget = document.querySelector('.quick-cart');
  var observer = null;

  function startObserver() {
    if (!observeTarget || observer) return;
    observer = new MutationObserver(function () {
      stopObserver();
      scheduleInit();
      setTimeout(startObserver, 0);
    });
    observer.observe(observeTarget, { childList: true, subtree: true });
  }

  function stopObserver() {
    if (observer) { observer.disconnect(); observer = null; }
  }

  startObserver();

  document.addEventListener('quick-cart:updated', function () {
    stopObserver();
    scheduleInit();
    setTimeout(startObserver, 0);
  });
})();
